import type { LeafRequest, PositionsTreeRequest, VmWindowRequest } from "./contracts";

export const positionsTreeQueryKeys = {
  tree: (request: PositionsTreeRequest) =>
    [
      "eex-positions-tree",
      request.grouping.join(","),
      buildPositionsTreeViewQueryFragment(request),
      request.includeMonths,
      request.fxBasis ?? "forward",
      // Only present while the VM column is on, so the cache identity of the
      // plain tree is unchanged.
      ...(request.vmWindow ? [buildVmWindowQueryFragment(request.vmWindow)] : []),
    ] as const,
  leafRoot: () => ["eex-positions-leaf"] as const,
  leaf: (request: LeafRequest) =>
    [
      "eex-positions-leaf",
      request.code,
      request.expYear,
      request.expMonth,
      request.mode,
      request.tradeOrigin,
      request.bucket.book,
      request.bucket.pair,
      request.bucket.trader,
      request.bucket.broker,
      request.bucket.venue,
      request.currency,
      request.settlementIndex,
      request.fxBasis ?? "forward",
    ] as const,
};

/**
 * Preserve the route's established view fragment because it is part of the
 * existing React Query cache identity as well as the request URL.
 */
export function buildPositionsTreeViewQueryFragment(
  request: Pick<PositionsTreeRequest, "view" | "includeOffBook">
): string {
  const { view } = request;
  if (view.kind === "interval") {
    return `&view=interval&from=${view.from}&to=${view.to}`;
  }
  if (view.kind === "active") {
    return `&view=active&offbook=${request.includeOffBook ? "1" : "0"}`;
  }
  return "&view=all";
}

export function buildVmWindowQueryFragment(vm: VmWindowRequest): string {
  const window = vm.kind === "range" ? `&vmFrom=${vm.from}&vmTo=${vm.to}` : `&vmLast=${vm.statements}`;
  return `${window}&vmBilateral=${vm.includeBilateral ? "1" : "0"}`;
}

export function buildPositionsTreeRequestPath(request: PositionsTreeRequest): string {
  return `/eex/positions/tree?grouping=${request.grouping.join(",")}&mode=${request.mode}${buildPositionsTreeViewQueryFragment(request)}&months=${request.includeMonths ? "1" : "0"}&fxBasis=${request.fxBasis ?? "forward"}${request.vmWindow ? buildVmWindowQueryFragment(request.vmWindow) : ""}`;
}

export type VmWindowPreset = "1D" | "7D" | "MTD" | "custom";

/**
 * Turn the toolbar's VM preset into a request window. 1D = the latest settlement
 * day (resolved server-side, since a calendar day may hold no settlement); 7D and
 * MTD are calendar windows ending today; custom uses the user's dates and is
 * null while they are inverted or incomplete.
 */
export function resolveVmWindowRequest(
  preset: VmWindowPreset,
  custom: { from: string; to: string },
  includeBilateral: boolean,
  today: string
): VmWindowRequest | null {
  if (preset === "1D") return { kind: "last", statements: 1, includeBilateral };
  if (preset === "7D") {
    const from = new Date(Date.parse(`${today}T00:00:00Z`) - 6 * 86_400_000).toISOString().slice(0, 10);
    return { kind: "range", from, to: today, includeBilateral };
  }
  if (preset === "MTD") return { kind: "range", from: `${today.slice(0, 8)}01`, to: today, includeBilateral };
  const iso = /^\d{4}-\d{2}-\d{2}$/;
  if (!iso.test(custom.from) || !iso.test(custom.to) || custom.from > custom.to) return null;
  return { kind: "range", from: custom.from, to: custom.to, includeBilateral };
}
